import { proxy, ref, subscribe } from "valtio"
import { SquadupDispatchedEvent, SquadupEventItem, SquadupShoppingCartItem, SquadupViewName } from "./squadup-events"
import { SquadupController } from "./squadup-controller"

export type ContainerCandidateArgs = {
  element: HTMLElement
  getPriority: (state: SquadupState, isVisible: boolean) => number
  onVisibleChange?: (visible: boolean) => void
  hacks?: (root: HTMLDivElement) => void
}

type ContainerCandidate = ContainerCandidateArgs & {
  id: number
  isVisible: boolean
}

let CANDIDATE_ID = 0

export function createSquadupState() {
  const state = proxy({
    hasManuallyOpened: false,
    currentView: null as SquadupViewName | null,
    open: false,
    viewingEvent: null as SquadupEventItem | null,
    quantity: 0,
    totals: {
      price: 0,
      fees: 0,
      subtotal: 0,
    },
    cart: null as SquadupShoppingCartItem[] | null,
    containerCandidates: [] as ContainerCandidate[],
    loading: false,
    registerContainerCandidate: (candidate: ContainerCandidateArgs) => {
      const id = CANDIDATE_ID++
      const candidateProxy = proxy({
        id,
        element: ref(candidate.element),
        getPriority: ref(candidate.getPriority),
        isVisible: false,
        onVisibleChange: candidate.onVisibleChange ? ref(candidate.onVisibleChange) : undefined,
      })
      state.containerCandidates.push(candidateProxy)
      return () => {
        state.containerCandidates = state.containerCandidates.filter((item) => item.id !== id)
      }
    },
    applyHacks: null as null | ((root: HTMLDivElement) => void),
    dispatchEvent(e: SquadupDispatchedEvent) {
      switch (e.type) {
        case "createSquadupEmbed":
          state.open = true
          break
        case "viewChanged":
          if (e.data.currentView === "eventList") {
            this.open = false
            this.viewingEvent = null
            break
          }
          state.open = true
          state.currentView = e.data.currentView
          if (e.data.currentView !== "eventDetail" && e.data.currentView !== "seatingChart") {
            state.viewingEvent = null
          }
          break
        case "eventLoaded":
          state.open = true
          state.viewingEvent = e.data.event
          break
        case "destroySquadupEmbed":
          state.open = false
          state.currentView = null
          state.viewingEvent = null
          break
        case "shoppingCartQuantityUpdated":
          state.quantity = e.data
          if (e.data === 0) {
            state.cart = []
          }
          break
        case "shoppingCartUpdated":
          state.cart = e.data
          break
        case "orderSuccessful":
          state.open = true
          break
      }
    },
  })
  subscribe(state, () => {
    const isOpen = state.open && state.hasManuallyOpened
    const element = SquadupController.getMain().element
    const sortedCandidates = state.containerCandidates
      .map((item) => {
        const priority = item.getPriority(state, item.isVisible)
        return {
          item,
          priority,
        }
      })
      .sort((a, b) => {
        return b.priority - a.priority
      })
    let target = null as HTMLElement | null
    sortedCandidates.forEach(({ item }, index) => {
      let visible = index === 0 && isOpen
      if (item.isVisible !== visible) {
        item.isVisible = visible
      }
      if (visible) {
        target = item.element
      }
      item.onVisibleChange?.(visible)
    })
    if (target) {
      target.appendChild(element)
    } else {
      document.body.appendChild(element)
    }
  })
  return state
}

export type SquadupState = ReturnType<typeof createSquadupState>
